import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../utils/alert";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  isModalOpen: false,
  isSubmitting: false,

  editMode: false,
  editId: null,

  categoriesList: [],

  form: {
    name: "",
    type: "expense", // Default to expense
    parent_id: "",
    description: "",
  },

  dataTableInstance: null,

  async initCategories() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Proceed with normal initialization
      await this.loadTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  // Dynamic filter to show only parent categories of the selected type
  get availableParents() {
    return this.categoriesList.filter(
      (c) =>
        c.parent_id === null &&
        c.type === this.form.type &&
        c.id !== this.editId,
    );
  },

  // Reset parent_id when the type changes to prevent type mismatches
  handleTypeChange() {
    this.form.parent_id = "";
  },

  getTypeBadge(type) {
    if (type === "income") {
      return `<span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">Receita</span>`;
    }
    return `<span class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">Despesa</span>`;
  },

  async loadTable() {
    try {
      const response = await axios.get("/api/client/financial/categories");
      this.categoriesList = response.data.data;

      if (this.dataTableInstance) {
        this.dataTableInstance.destroy();
      }

      const tbody = document.querySelector("#categoriesTable tbody");
      tbody.innerHTML = this.categoriesList
        .map((category) => {
          const parentName = category.parent
            ? category.parent.name
            : '<span class="text-slate-400 italic">Categoria Principal</span>';

          return `
            <tr class="hover:bg-slate-50/50 transition-colors">
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${category.name}</p>
              </td>
              <td class="py-4 px-4 xl:px-6">
                ${this.getTypeBadge(category.type)}
              </td>
              <td class="py-4 px-4 xl:px-6 text-slate-600 text-sm">
                ${parentName}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-category', {detail: ${category.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-category', {detail: ${category.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#categoriesTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} categorias",
          noRows: "Nenhuma categoria cadastrada",
          noResults: "Nenhuma categoria encontrada",
          searchTitle: "Buscar...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      type: "expense",
      parent_id: "",
      description: "",
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const category = this.categoriesList.find((c) => c.id === id);

    this.form = {
      name: category.name,
      type: category.type,
      parent_id: category.parent_id || "",
      description: category.description || "",
    };
    this.isModalOpen = true;
  },

  async submitForm() {
    if (!this.form.name || this.form.name.trim() === "") {
      showAlert("Por favor, preencha o Nome da Categoria.", "warning");
      return;
    }

    this.isSubmitting = true;

    const payload = {
      ...this.form,
      parent_id: this.form.parent_id === "" ? null : this.form.parent_id,
    };

    try {
      if (this.editMode) {
        await axios.put(
          `/api/client/financial/categories/${this.editId}`,
          payload,
        );
        showAlert("Categoria atualizada com sucesso!", "success");
      } else {
        await axios.post("/api/client/financial/categories", payload);
        showAlert("Categoria cadastrada com sucesso!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.error
      ) {
        showAlert(error.response.data.error, "warning");
      } else {
        handleRequestError(error, "Erro ao guardar a categoria.");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Categoria",
      "Tem certeza? O histórico financeiro será mantido, mas ela não poderá ser usada em novos lançamentos.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/financial/categories/${id}`);
        await this.loadTable();
        showAlert("Categoria removida com sucesso.", "success");
      } catch (error) {
        if (
          error.response &&
          error.response.status === 422 &&
          error.response.data.error
        ) {
          showAlert(error.response.data.error, "warning");
        } else {
          handleRequestError(error, "Erro ao excluir a categoria.");
        }
      }
    }
  },
});
